// src/hooks/useMultiplayerGame.ts (FIXED - WITH PIECE VALUE CALCULATION FOR GUESTS)
import { useEffect } from "react";
import { Piece } from "../types/Board";
import { PIECE_DEFINITIONS } from "../data/pieceDefinitions.ts";
import { MoveHistoryManager } from "../utils/MoveHistoryManager.ts";

interface UseMultiplayerGameProps {
  gameMode: "local" | "vsAI" | "online";
  clockEnabled: boolean;
  clockDuration: number;
  historyManager: MoveHistoryManager;
  boardRows: number;
  receivedGameState: any;
  setOnOpponentMove: (handler: (data: any) => void) => void;
  setOnGameStart: (handler: (data: any) => void) => void;
  setOnGameFinished: (handler: (data: any) => void) => void;
  setOnOpponentDisconnected: (handler: () => void) => void;
  setOnOpponentJoinedRoom: (handler: (data: any) => void) => void;
  onOpponentMove: (
    pieces: Map<string, Piece>,
    currentTurn: "white" | "black",
    capturedPiece?: Piece
  ) => void;
  onGameStart: (boardSize?: number, preset?: string) => void;
  onGameEnd: () => void;
  setShowLobby: (show: boolean) => void;
  setHasActiveOnlineGame: (active: boolean) => void;
  setBoardRows: (rows: number) => void;
  setBoardCols: (cols: number) => void;
  setGamePieces: (pieces: Map<string, Piece>) => void;
  setCurrentGameId: (id: string) => void;
  setCapturedPieces: (
    updater: (
      prev: { white: Piece[]; black: Piece[] }
    ) => { white: Piece[]; black: Piece[] }
  ) => void;
  setMoveHistory: (history: any[]) => void;
  resetClock: () => void;
  gamePresets: any[];
  // Optional callback for showing notifications instead of alerts
  onNotification?: (type: "success" | "error" | "info" | "warning", title: string, message: string) => void;
}

// ⭐ NEW HELPER FUNCTION: Calculate piece value based on board size
const calculatePieceValue = (piece: Piece, boardSize: number): number => {
  try {
    const pieceValuesModule = require("../data/pieceValues.ts");
    const pieceDef = PIECE_DEFINITIONS[piece.type.toLowerCase()];
    
    if (!pieceDef) {
      console.warn(`No definition found for piece: ${piece.type}`);
      return 0;
    }
    
    const abbreviation = pieceDef.abbreviation.toUpperCase();
    const boardSizeStr = `${boardSize}×${boardSize}`;
    const pieceValues = pieceValuesModule.PIECE_VALUES?.[abbreviation];

    if (pieceValues) {
      const valueEntry = pieceValues.find(
        (v: any) => v.boardSize === boardSizeStr
      );
      if (valueEntry) {
        return valueEntry.value;
      }
    }
    
    // Fallback to generic value from definition
    if (pieceDef.value) {
      return pieceDef.value;
    }
    
    return 0;
  } catch (error) {
    console.warn(`Error calculating value for piece ${piece.type}:`, error);
    return 0;
  }
};

// ⭐ NEW HELPER FUNCTION: Enhance piece with calculated properties
const enhancePiece = (piece: Piece, boardSize: number): Piece => {
  const pieceDef = PIECE_DEFINITIONS[piece.type.toLowerCase()];
  
  return {
    ...piece,
    abbreviation: pieceDef?.abbreviation || piece.type.substring(0, 3).toUpperCase(),
    rules: pieceDef?.rules || piece.rules || [],
    value: calculatePieceValue(piece, boardSize),
  };
};

export const useMultiplayerGame = ({
  gameMode,
  clockEnabled,
  clockDuration,
  historyManager,
  boardRows,
  receivedGameState,
  setOnOpponentMove,
  setOnGameStart,
  setOnGameFinished,
  setOnOpponentDisconnected,
  setOnOpponentJoinedRoom,
  onOpponentMove,
  onGameStart,
  onGameEnd,
  setShowLobby,
  setHasActiveOnlineGame,
  setBoardRows,
  setBoardCols,
  setGamePieces,
  setCurrentGameId,
  setCapturedPieces,
  setMoveHistory,
  resetClock,
  gamePresets,
  onNotification,
}: UseMultiplayerGameProps) => {
  
  // ============================================================================
  // ⭐ GUEST BOARD INITIALIZATION (ENHANCED WITH PIECE VALUE CALCULATION)
  // ============================================================================
  useEffect(() => {
    if (gameMode !== "online" || !receivedGameState) return;

    console.log("🎮 ========== GUEST BOARD INITIALIZATION ==========");
    console.log("📦 Received gameState:", receivedGameState);

    // ⭐ GUARD: Only run once per receivedGameState
    // Use a ref to track if we've already initialized this gameState
    const gameStateId = receivedGameState.boardSize + '-' + (receivedGameState.preset || 'none');
    console.log("🔍 Initializing for gameState ID:", gameStateId);

    // STEP 1: Set board size FIRST
    if (receivedGameState.boardSize) {
      const boardSize = receivedGameState.boardSize;
      console.log(`📐 STEP 1: Setting guest board to ${boardSize}×${boardSize}`);
      
      setBoardRows(boardSize);
      setBoardCols(boardSize);
      
      console.log(`✅ Board dimensions set to ${boardSize}×${boardSize}`);
    } else {
      console.error("❌ CRITICAL ERROR: No boardSize in receivedGameState!");
      console.log("receivedGameState contents:", receivedGameState);
      return; // ⭐ Exit early if no board size
    }

    // STEP 2: Find matching preset (but don't set it - avoid triggering reset!)
    if (receivedGameState.preset) {
      const presetId = receivedGameState.preset;
      console.log(`🎯 STEP 2: Identifying preset: "${presetId}"`);
      
      const preset = gamePresets.find(
        (p: any) => {
          const matchesId = p.id === presetId;
          const matchesVariantId = p.variantId === presetId;
          const matchesSetup = p.setup === presetId;
          const matchesBySize = receivedGameState.boardSize && p.rows === receivedGameState.boardSize;
          
          if (matchesId || matchesVariantId || matchesSetup || matchesBySize) {
            console.log(`   ✓ Found preset: ${p.name}`);
            return true;
          }
          return false;
        }
      );
      
      if (preset) {
        // ⭐ DON'T call setCurrentGameId here - it triggers board reset!
        console.log(`✅ Preset identified: ${preset.name} (id: ${preset.id})`);
        console.log(`   (Not setting preset to avoid triggering board reset)`);
      } else {
        console.warn(`⚠️ No preset found matching "${presetId}"`);
      }
    } else {
      console.warn("⚠️ No preset ID in receivedGameState");
    }

    // STEP 3: Load pieces with value calculation (⭐ FIXED)
    if (receivedGameState.pieces && receivedGameState.boardSize) {
      console.log(`♟️ STEP 3: Loading and enhancing pieces from receivedGameState`);
      
      const boardSize = receivedGameState.boardSize;
      const enhancedPiecesMap = new Map<string, Piece>();
      
      // Convert array to Map and enhance each piece
      for (const [key, piece] of receivedGameState.pieces) {
        const enhancedPiece = enhancePiece(piece, boardSize);
        enhancedPiecesMap.set(key, enhancedPiece);
      }
      
      setGamePieces(enhancedPiecesMap);
      
      console.log(`✅ Successfully loaded and enhanced ${enhancedPiecesMap.size} pieces for guest`);
      console.log("First 3 pieces with values:");
      Array.from(enhancedPiecesMap.entries()).slice(0, 3).forEach(([key, piece]) => {
        console.log(`   ${key}: ${piece.type} at (${piece.position.row},${piece.position.col}) - value: ${piece.value || 0}`);
      });
    } else {
      console.warn("⚠️ No pieces or boardSize in receivedGameState");
    }

    // STEP 4: Initialize captured pieces if provided
    if (receivedGameState.capturedPieces) {
      console.log("📦 STEP 4: Loading captured pieces");
      setCapturedPieces(() => receivedGameState.capturedPieces);
    }

    // STEP 5: Initialize move history if provided
    if (receivedGameState.moveHistory && Array.isArray(receivedGameState.moveHistory)) {
      console.log(`📜 STEP 5: Loading move history (${receivedGameState.moveHistory.length} moves)`);
      setMoveHistory(receivedGameState.moveHistory);
    }

    console.log("✅ ========== GUEST BOARD INITIALIZATION COMPLETE ==========");
    console.log(`   Board Size: ${receivedGameState.boardSize}×${receivedGameState.boardSize}`);
    console.log(`   Preset: ${receivedGameState.preset || 'not set'}`);
    console.log(`   Pieces Count: ${receivedGameState.pieces?.length || 0}`);
    console.log("==========================================");
    
  }, [receivedGameState, gameMode]);

  // ============================================================================
  // MULTIPLAYER EVENT HANDLERS
  // ============================================================================
  useEffect(() => {
    if (gameMode !== "online") return;

    console.log("🌐 Setting up multiplayer event handlers");

    // ============================================================================
    // OPPONENT MOVE HANDLER
    // ============================================================================
    setOnOpponentMove((data) => {
      console.log("========================================");
      console.log("👥 OPPONENT MOVE RECEIVED");
      console.log("========================================");
      console.log("📦 Full data:", data);

      if (data.gameState && data.gameState.pieces) {
        console.log("📊 Received pieces array length:", data.gameState.pieces.length);
        console.log("📊 First 3 pieces from server:");
        data.gameState.pieces.slice(0, 3).forEach(([key, piece]: [string, any]) => {
          console.log(`   ${key}: ${piece.type} at (${piece.position.row},${piece.position.col})`);
        });
        
        // ⭐ CRITICAL FIX: Enhance all pieces before creating the Map
        console.log("🔄 Enhancing opponent's board state...");
        const enhancedPiecesMap = new Map<string, Piece>();
        
        // Enhance each piece with calculated properties
        for (const [key, piece] of data.gameState.pieces) {
          const enhancedPiece = enhancePiece(piece, boardRows);
          
          // ⭐ VERIFY: Check if piece position matches key
          const expectedKey = `${piece.position.row}-${piece.position.col}`;
          if (key !== expectedKey) {
            console.error(`❌ KEY MISMATCH: key=${key} but position=(${piece.position.row},${piece.position.col})`);
          }
          
          enhancedPiecesMap.set(key, enhancedPiece);
        }
        
        console.log(`✅ Enhanced ${enhancedPiecesMap.size} pieces from opponent move`);
        console.log("📊 First 3 enhanced pieces:");
        Array.from(enhancedPiecesMap.entries()).slice(0, 3).forEach(([key, piece]) => {
          console.log(`   ${key}: ${piece.type} at (${piece.position.row},${piece.position.col})`);
        });

        // ⭐ FIXED: Declare capturedPiece at top level to avoid scope issues
        let capturedPiece: Piece | undefined = undefined;

        // Reconstruct opponent's move and add it to history
        if (data.move) {
          console.log("🎯 Move details:");
          console.log(`   Piece: ${data.move.piece} (${data.move.color})`);
          console.log(`   From: (${data.move.from.row},${data.move.from.col})`);
          console.log(`   To: (${data.move.to.row},${data.move.to.col})`);
          console.log(`   Captured: ${data.move.captured || 'none'}`);

          // Create opponent's piece object
          const opponentPiece: Piece = {
            type: data.move.piece,
            color: data.move.color,
            position: data.move.from,
            abbreviation:
              PIECE_DEFINITIONS[data.move.piece.toLowerCase()]?.abbreviation ||
              data.move.piece.substring(0, 3).toUpperCase(),
            hasMoved: true,
            rules:
              PIECE_DEFINITIONS[data.move.piece.toLowerCase()]?.rules || [],
          };

          if (data.move.captured) {
            const capturedColor: "white" | "black" =
              data.move.color === "white" ? "black" : "white";
            const capturedPieceDef =
              PIECE_DEFINITIONS[data.move.captured.toLowerCase()];

            // ⭐ Calculate piece value using helper function
            const pieceValue = capturedPieceDef 
              ? calculatePieceValue({ 
                  type: data.move.captured, 
                  color: capturedColor,
                  position: data.move.to,
                  abbreviation: capturedPieceDef.abbreviation,
                  hasMoved: true,
                  rules: capturedPieceDef.rules || [],
                } as Piece, boardRows)
              : 0;

            capturedPiece = {
              type: data.move.captured,
              color: capturedColor,
              position: data.move.to,
              abbreviation:
                capturedPieceDef?.abbreviation ||
                data.move.captured.substring(0, 3).toUpperCase(),
              hasMoved: true,
              rules: capturedPieceDef?.rules || [],
              value: pieceValue,
            };

            // Add captured piece to local state
            setCapturedPieces((prev) => {
              const updated = { ...prev };
              if (capturedColor === "white") {
                updated.white = [...prev.white, capturedPiece!];
                console.log(
                  "➕ Added white piece to captured:",
                  capturedPiece!.type,
                  "value:",
                  pieceValue
                );
              } else {
                updated.black = [...prev.black, capturedPiece!];
                console.log(
                  "➕ Added black piece to captured:",
                  capturedPiece!.type,
                  "value:",
                  pieceValue
                );
              }
              return updated;
            });
          }

          // Add opponent's move to local history
          historyManager.addMove(
            opponentPiece,
            data.move.from,
            data.move.to,
            capturedPiece,
            false,
            false,
            false,
            undefined,
            undefined
          );

          // Update display with combined history
          const updatedHistory = historyManager.getMovesWithNumbers();
          setMoveHistory(updatedHistory);

          console.log("✅ Added opponent move to our history");
        }

        // ⭐ CRITICAL: Pass the enhanced pieces map to trigger board update
        console.log("🎨 Updating board display with enhanced pieces...");
        console.log("📊 Pieces count being set:", enhancedPiecesMap.size);
        console.log("🔄 New turn:", data.gameState.currentTurn);
        onOpponentMove(enhancedPiecesMap, data.gameState.currentTurn, capturedPiece);
        console.log("✅ Board update complete");
        console.log("========================================");
      }
    });

    // ============================================================================
    // GAME START HANDLER
    // ============================================================================
    setOnGameStart((data) => {
      console.log("🎮 ========== GAME START RECEIVED ==========");
      console.log("📦 Full game start data:", JSON.stringify(data, null, 2));
      
      setShowLobby(false);
      setHasActiveOnlineGame(true);

      if (!data.gameState) {
        console.warn("⚠️ No gameState provided in game start - using defaults");
        onGameStart();
        if (clockEnabled) {
          resetClock();
        }
        return;
      }

      console.log("📋 Initializing board from gameState");

      // STEP 1: Set board size FIRST
      if (data.gameState.boardSize) {
        const boardSize = data.gameState.boardSize;
        console.log(`📐 STEP 1: Setting board dimensions to ${boardSize}×${boardSize}`);
        
        setBoardRows(boardSize);
        setBoardCols(boardSize);
        
        console.log(`✅ Board dimensions set successfully to ${boardSize}×${boardSize}`);
      } else {
        console.error("❌ CRITICAL ERROR: No boardSize in gameState!");
        console.log("gameState contents:", data.gameState);
      }

      // STEP 2: Identify game preset (but don't set it to avoid reset)
      if (data.gameState.preset) {
        const presetId = data.gameState.preset;
        console.log(`🎯 STEP 2: Identifying preset: "${presetId}"`);
        
        const preset = gamePresets.find(
          (p: any) => {
            const matchesId = p.id === presetId;
            const matchesVariantId = p.variantId === presetId;
            const matchesSetup = p.setup === presetId;
            const matchesBySize = data.gameState.boardSize && p.rows === data.gameState.boardSize;
            
            if (matchesId || matchesVariantId || matchesSetup || matchesBySize) {
              console.log(`   ✓ Found preset: ${p.name}`);
              return true;
            }
            return false;
          }
        );
        
        if (preset) {
          // ⭐ DON'T call setCurrentGameId - it would trigger board reset!
          console.log(`✅ Preset identified: ${preset.name} (id: ${preset.id})`);
          console.log(`   (Not setting preset to avoid triggering board reset)`);
        } else {
          console.warn(`⚠️ WARNING: No preset found matching "${presetId}"`);
        }
      }

      // STEP 3: Set pieces with value calculation (⭐ FIXED)
      if (data.gameState.pieces && data.gameState.boardSize) {
        console.log(`♟️ STEP 3: Loading and enhancing pieces from gameState`);
        
        const boardSize = data.gameState.boardSize;
        const enhancedPiecesMap = new Map<string, Piece>();
        
        // Convert array to Map and enhance each piece
        for (const [key, piece] of data.gameState.pieces) {
          const enhancedPiece = enhancePiece(piece, boardSize);
          enhancedPiecesMap.set(key, enhancedPiece);
        }
        
        setGamePieces(enhancedPiecesMap);
        
        console.log(`✅ Successfully loaded and enhanced ${enhancedPiecesMap.size} pieces`);
        console.log("First 3 pieces with values:");
        Array.from(enhancedPiecesMap.entries()).slice(0, 3).forEach(([key, piece]) => {
          console.log(`   ${key}: ${piece.type} at (${piece.position.row},${piece.position.col}) - value: ${piece.value || 0}`);
        });
      } else {
        console.error("❌ CRITICAL ERROR: No pieces or boardSize in gameState!");
      }

      // STEP 4: Notify parent component
      console.log("📢 Notifying parent component of game start");
      onGameStart(data.gameState.boardSize, data.gameState.preset);

      // STEP 5: Reset clock if enabled
      if (clockEnabled) {
        console.log("⏱️ Resetting game clock");
        resetClock();
      }

      console.log("✅ ========== GAME START COMPLETE ==========");
      console.log(`   Board Size: ${data.gameState.boardSize}×${data.gameState.boardSize}`);
      console.log(`   Preset: ${data.gameState.preset || 'not set'}`);
      console.log(`   Pieces Count: ${data.gameState.pieces?.length || 0}`);
      console.log("==========================================");
    });

    // ============================================================================
    // GAME FINISHED HANDLER
    // ============================================================================
    setOnGameFinished((data) => {
      console.log("🏁 Game finished:", data);

      const message = data.winner === "draw"
        ? "Game ended in a draw!"
        : `Game Over! ${data.winner.charAt(0).toUpperCase() + data.winner.slice(1)} wins!`;

      if (onNotification) {
        onNotification("info", "Game Over", message);
      }
      setHasActiveOnlineGame(false);
      setShowLobby(true);
      onGameEnd();
    });

    // ============================================================================
    // OPPONENT DISCONNECTED HANDLER
    // ============================================================================
    setOnOpponentDisconnected(() => {
      console.log("👋 Opponent disconnected from the game!");
      if (onNotification) {
        onNotification("warning", "Opponent Disconnected", "Your opponent has disconnected from the game.");
      }
      setHasActiveOnlineGame(false);
      setShowLobby(true);
      onGameEnd();
    });

    // ============================================================================
    // OPPONENT JOINED ROOM HANDLER
    // ============================================================================
    setOnOpponentJoinedRoom((data) => {
      console.log("👥 Opponent joined your game room:", data);
    });

    // ============================================================================
    // CLEANUP
    // ============================================================================
    return () => {
      console.log("🌐 Cleaning up multiplayer event handlers");
    };
  }, [
    gameMode,
    clockEnabled,
    clockDuration,
    historyManager,
    boardRows,
    gamePresets,
    setOnOpponentMove,
    setOnGameStart,
    setOnGameFinished,
    setOnOpponentDisconnected,
    setOnOpponentJoinedRoom,
    onOpponentMove,
    onGameStart,
    onGameEnd,
    setShowLobby,
    setHasActiveOnlineGame,
    setBoardRows,
    setBoardCols,
    setGamePieces,
    setCurrentGameId,
    setCapturedPieces,
    setMoveHistory,
    resetClock,
  ]);

  return {
    // This hook primarily sets up event handlers
  };
};